import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from 'react-daisyui';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  DocumentTextIcon,
  LightBulbIcon,
  HandThumbUpIcon,
  ShieldCheckIcon,
  CircleStackIcon,
  ArrowLeftIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import useAdminSessionDetail from '@/api/admin/useAdminSessionDetail.ts';
import useAdminOverride from '@/api/admin/useAdminOverride.ts';
import useAuditExport from '@/api/useAuditExport.ts';
import GovernanceChainList from '@/components/ui/GovernanceChainList/GovernanceChainList.tsx';
import LoaderWrapper from '@/components/ui/LoaderWrapper/LoaderWrapper.tsx';
import Routes from '@/router/Routes.ts';
import { cn } from '@/utils/classNames.ts';
import type { Tables } from '$/database.types';

// ── local types ───────────────────────────────────────────────────────────────

type LedgerEntry = Tables<'ledger_entries'>;

type IntentPayload = { issue_title?: string; issue_text?: string };

type DiagnosticAiOutput = {
  diagnostic_summary?: string;
  assumption_record?: string;
};

type DiagnosticPayload = { user_reasoning_record?: string };

type RecommendationAiOutput = {
  recommended_direction?: { lead?: string; detail?: string };
  root_cause_diagnosis?: { lead?: string; detail?: string };
  action_plan?: { lead?: string; bullets?: string[] };
};

type CompassFilter = {
  name?: string;
  status?: 'passed' | 'caution' | 'escalation';
  framework_connection?: string;
  explanation?: string;
  next_session_recommendation?: string;
};

type GovernanceAiOutput = {
  compass_filters?: CompassFilter[];
  governance_helper_copy?: string;
};

// ── helpers ───────────────────────────────────────────────────────────────────

function getStepAi<T>(entries: LedgerEntry[], step: string): T | null {
  const e = entries.find((x) => x.step === step);
  return e ? ((e.ai_output as T) ?? null) : null;
}

function getStepPayload<T>(entries: LedgerEntry[], step: string): T | null {
  const e = entries.find((x) => x.step === step);
  return e ? ((e.payload as T) ?? null) : null;
}

const COMPASS_STYLE = {
  passed: { card: 'bg-[#edfbf4]', title: 'text-[#00874c]', tag: 'Passed' },
  caution: { card: 'bg-[#fffdf0]', title: 'text-[#b38600]', tag: 'Caution' },
  escalation: { card: 'bg-[#fff1f0]', title: 'text-[#d93025]', tag: 'Escalation' },
} as const;

function compassStyle(status?: string) {
  return COMPASS_STYLE[(status as keyof typeof COMPASS_STYLE) ?? 'caution'] ?? COMPASS_STYLE.caution;
}

const LIFECYCLE_LABELS: Record<string, string> = {
  execution_asset_produced: 'Completed',
  governance_evaluated: 'Under Review',
  escalated: 'Escalated',
  framework_routed: 'In Progress',
  intent_captured: 'Started',
};

function sessionId(id: string): string {
  const year = new Date().getFullYear();
  const short = id.replace(/-/g, '').slice(0, 4).toUpperCase();
  return `CMD-${year}-${short}`;
}

// ── override form schema ──────────────────────────────────────────────────────

const overrideSchema = z.object({
  override_note: z.string().min(10, 'Note must be at least 10 characters'),
});
type OverrideForm = z.infer<typeof overrideSchema>;

const AdminSessionDetailPage: FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading, isError } = useAdminSessionDetail(id ?? null);
  const override = useAdminOverride();
  const { mutate: exportRecord, isPending: isExporting } = useAuditExport();
  const [exportError, setExportError] = useState<string | null>(null);
  const [disposition, setDisposition] = useState<'approved' | 'returned'>('approved');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<OverrideForm>({ resolver: zodResolver(overrideSchema) });

  if (isLoading) return <LoaderWrapper isLoading />;
  if (isError || !data) {
    return <p className="font-dm text-sm text-center py-20 text-[#2b3440]/50">Session not found.</p>;
  }

  const { decision, ledger_entries, approval_states } = data;

  const intent = getStepPayload<IntentPayload>(ledger_entries, 'intent_captured');
  const diagnosticAi = getStepAi<DiagnosticAiOutput>(ledger_entries, 'diagnostic');
  const diagnosticPayload = getStepPayload<DiagnosticPayload>(ledger_entries, 'diagnostic');
  const recommendation = getStepAi<RecommendationAiOutput>(ledger_entries, 'recommendation');
  const governance = getStepAi<GovernanceAiOutput>(ledger_entries, 'governance_check');

  const pendingApproval = approval_states.find((s) => s.status === 'pending');
  const escalationEntry = approval_states.find((s) => s.escalation_reason);

  const onSubmitOverride = handleSubmit((values) => {
    if (!pendingApproval) return;
    override.mutate(
      {
        decision_id: decision.id,
        approval_state_id: pendingApproval.id,
        disposition,
        override_note: values.override_note,
      },
      {
        onSuccess: () => {
          reset();
          navigate(Routes.ADMIN);
        },
      }
    );
  });

  const lifecycleLabel = LIFECYCLE_LABELS[decision.status ?? ''] ?? decision.status ?? '—';

  return (
    <div className="flex flex-col gap-8">
      {/* Page header — matches AdminSessionsPage / AdminUsersPage pattern */}
      <div className="flex items-center px-6 py-4 bg-[#f4f9ff] rounded-xl">
        <div className="flex flex-col gap-1">
          <h1 className="font-newsreader text-[28px] font-extrabold leading-[125%] text-[rgba(0,0,0,0.87)]">
            Session Detail
          </h1>
          <p className="font-dm text-[14px] font-normal leading-[150%] tracking-[0.15px] text-[rgba(0,0,0,0.6)]">
            Review the full decision record for this session.
          </p>
        </div>
        {decision.status === 'execution_asset_produced' && (
          <button
            onClick={() => {
              setExportError(null);
              exportRecord(decision.id, {
                onError: (err) => {
                  setExportError(err instanceof Error ? err.message : 'Export failed');
                },
              });
            }}
            disabled={isExporting}
            className="ml-auto px-4 py-2 rounded-xl bg-[#0f1b2a] text-white font-dm font-bold text-sm disabled:opacity-50 hover:opacity-80 transition-opacity"
          >
            {isExporting ? 'Exporting…' : 'Export Audit'}
          </button>
        )}
      </div>

      {exportError && (
        <Alert status="error" className="rounded-xl">
          <span className="font-dm text-sm">{exportError}</span>
        </Alert>
      )}

      {/* Metadata pill-bar */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-6 py-4 rounded-2xl border border-[rgba(204,204,204,0.5)] bg-white">
        <MetaItem icon={<DocumentTextIcon className="w-4 h-4 shrink-0" />} label={decision.issue_title ?? '—'} />
        <Sep />
        <MetaItem
          icon={<CircleStackIcon className="w-4 h-4 shrink-0" />}
          label={<>{decision.primary_framework ?? '—'} Applied</>}
        />
        <Sep />
        <MetaItem
          icon={<ShieldCheckIcon className="w-4 h-4 shrink-0" />}
          label={
            <>
              Governance Score{' '}
              <strong>
                {decision.governance_score !== null && decision.governance_score !== undefined
                  ? decision.governance_score
                  : '—'}
              </strong>
            </>
          }
        />
        <Sep />
        <MetaItem
          icon={<InformationCircleIcon className="w-4 h-4 shrink-0" />}
          label={<strong>{lifecycleLabel}</strong>}
        />
        <Sep />
        <MetaItem
          label={
            decision.completed_at
              ? dayjs(decision.completed_at).format('MMM D, YYYY')
              : dayjs(decision.created_at).format('MMM D, YYYY')
          }
        />
      </div>

      {/* Issue Framing */}
      {intent?.issue_text && (
        <Card icon={<DocumentTextIcon className="w-5 h-5" />} title="Issue Framing">
          <p className="font-dm text-sm text-[rgba(0,0,0,0.7)] leading-relaxed">{intent.issue_text}</p>
        </Card>
      )}

      {/* Diagnostic Interpretation */}
      {diagnosticAi?.diagnostic_summary && (
        <Card icon={<LightBulbIcon className="w-5 h-5" />} title="Diagnostic Interpretation">
          <p className="font-dm text-sm text-[rgba(0,0,0,0.7)] leading-relaxed">{diagnosticAi.diagnostic_summary}</p>
        </Card>
      )}

      {/* ZDE Learning Note */}
      {diagnosticPayload?.user_reasoning_record && (
        <div className="flex flex-col gap-3 px-6 py-5 rounded-2xl border border-[rgba(204,204,204,0.5)] bg-white">
          <p className="font-dm text-sm font-bold text-[rgba(0,0,0,0.87)]">ZDE Learning Note</p>
          <blockquote className="font-dm text-sm italic text-[rgba(0,0,0,0.7)] border-l-2 border-[rgba(0,0,0,0.15)] pl-4 leading-relaxed">
            "{diagnosticPayload.user_reasoning_record}"
          </blockquote>
          <p className="font-dm text-xs text-[rgba(0,0,0,0.4)] italic">
            Captured during diagnostic confirmation · Step: CLARITY™
          </p>
        </div>
      )}

      {/* Recommendation */}
      {recommendation && (
        <Card icon={<HandThumbUpIcon className="w-5 h-5" />} title="Recommendation">
          {recommendation.recommended_direction?.lead && (
            <p className="font-dm text-sm text-[rgba(0,0,0,0.7)] leading-relaxed">
              {recommendation.recommended_direction.lead}
            </p>
          )}

          {recommendation.root_cause_diagnosis?.lead && (
            <div className="mt-4">
              <p className="font-dm text-xs font-bold text-[rgba(0,0,0,0.45)] uppercase tracking-wider mb-1">
                Root Cause
              </p>
              <p className="font-dm text-sm text-[rgba(0,0,0,0.7)] leading-relaxed">
                {recommendation.root_cause_diagnosis.lead}
              </p>
            </div>
          )}

          {recommendation.action_plan && (
            <div className="mt-4">
              <p className="font-dm text-xs font-bold text-[rgba(0,0,0,0.45)] uppercase tracking-wider mb-1">
                Action Plan
              </p>
              {recommendation.action_plan.lead && (
                <p className="font-dm text-sm text-[rgba(0,0,0,0.7)] leading-relaxed mb-1">
                  {recommendation.action_plan.lead}
                </p>
              )}
              {(recommendation.action_plan.bullets ?? []).map((b, i) => (
                <p key={i} className="font-dm text-sm text-[rgba(0,0,0,0.7)]">
                  • {b}
                </p>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* COMPASS™ Review */}
      {governance?.compass_filters && governance.compass_filters.length > 0 && (
        <div className="flex flex-col gap-5 px-6 py-5 rounded-2xl border border-[rgba(204,204,204,0.5)] bg-white">
          <div>
            <h2 className="font-newsreader text-[22px] font-extrabold text-[rgba(0,0,0,0.87)]">COMPASS™ Review</h2>
            {governance.governance_helper_copy && (
              <p className="font-dm text-sm text-[rgba(0,0,0,0.6)] mt-1 leading-relaxed">
                {governance.governance_helper_copy}
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {governance.compass_filters.map((f, i) => {
              const s = compassStyle(f.status);
              return (
                <div key={i} className={cn('rounded-xl p-4 flex flex-col gap-2', s.card)}>
                  <p className={cn('font-dm text-sm font-bold', s.title)}>
                    {f.name} – {s.tag}
                  </p>
                  {f.framework_connection && (
                    <p className="font-dm text-xs font-semibold text-[rgba(0,0,0,0.45)]">ZDE Framework Connection</p>
                  )}
                  {f.explanation && (
                    <p className="font-dm text-xs text-[rgba(0,0,0,0.65)] leading-relaxed">{f.explanation}</p>
                  )}
                  {f.next_session_recommendation && (
                    <p className="font-dm text-xs text-[rgba(0,0,0,0.45)] leading-relaxed italic mt-1">
                      {f.next_session_recommendation}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Escalation Status */}
      {(pendingApproval || escalationEntry) && (
        <div className="flex flex-col gap-3 px-6 py-5 rounded-2xl border border-[rgba(204,204,204,0.5)] bg-white">
          <div className="flex flex-wrap items-baseline gap-2">
            <h2 className="font-newsreader text-[22px] font-extrabold text-[rgba(0,0,0,0.87)]">Escalation Status –</h2>
            <span className="font-newsreader text-[22px] font-extrabold text-[#b38600]">Marked for human review</span>
          </div>
          {escalationEntry?.escalation_reason && (
            <div>
              <p className="font-dm text-xs font-bold text-[rgba(0,0,0,0.45)] mb-1">Reason:</p>
              <p className="font-dm text-sm text-[rgba(0,0,0,0.7)]">{escalationEntry.escalation_reason}</p>
            </div>
          )}
        </div>
      )}

      {ledger_entries.length > 0 && (
        <div className="flex flex-col gap-4 px-6 py-5 rounded-2xl border border-[rgba(204,204,204,0.5)] bg-white">
          <div className="flex flex-col gap-1">
            <h2 className="font-newsreader text-[22px] font-extrabold text-[rgba(0,0,0,0.87)]">
              {t('governanceChain.title')}
            </h2>
            <p className="font-dm text-sm text-[rgba(0,0,0,0.55)]">{t('governanceChain.description')}</p>
          </div>
          <GovernanceChainList entries={ledger_entries} />
        </div>
      )}

      {/* Session Metadata */}
      <div className="flex flex-col gap-3 px-6 py-5 rounded-2xl border border-[rgba(204,204,204,0.5)] bg-white">
        <div className="flex items-center gap-2 text-[rgba(0,0,0,0.87)]">
          <DocumentTextIcon className="w-5 h-5" />
          <h2 className="font-newsreader text-[22px] font-extrabold">Session Metadata</h2>
        </div>
        <div className="flex flex-col gap-1 font-dm text-sm text-[rgba(0,0,0,0.7)]">
          <p>
            Framework: <strong>{decision.primary_framework ?? '—'}</strong>
          </p>
          <p>
            Governance Score: <strong>{decision.governance_score ?? '—'}</strong>
          </p>
          {decision.organizations?.name && (
            <p>
              Organization: <strong>{decision.organizations.name}</strong>
            </p>
          )}
          {decision.users?.email && (
            <p>
              User: <strong>{decision.users.email}</strong>
            </p>
          )}
          <p>
            Session ID: <strong>{sessionId(decision.id)}</strong>
          </p>
        </div>
      </div>

      {/* Admin Override (only when a pending escalation exists) */}
      {pendingApproval && (
        <form
          onSubmit={onSubmitOverride}
          className="flex flex-col gap-4 px-6 py-5 rounded-2xl border-2 border-[rgba(255,190,0,0.5)] bg-[#fffdf0]"
        >
          <div>
            <p className="font-dm text-sm font-bold text-[rgba(0,0,0,0.87)]">Admin Override</p>
            <p className="font-dm text-xs text-[rgba(0,0,0,0.45)] mt-0.5">{t('governanceChain.immutableNote')}</p>
          </div>

          <div className="flex gap-2">
            {(['approved', 'returned'] as const).map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDisposition(d)}
                className={cn(
                  'px-4 py-2 rounded-lg text-sm font-dm font-bold border transition-colors',
                  disposition === d
                    ? d === 'approved'
                      ? 'bg-[#00a96e] text-white border-[#00a96e]'
                      : 'bg-[#d93025] text-white border-[#d93025]'
                    : 'bg-white text-[rgba(0,0,0,0.45)] border-[rgba(204,204,204,0.7)] hover:border-[rgba(0,0,0,0.3)]'
                )}
              >
                {d === 'approved' ? 'Approve' : 'Return'}
              </button>
            ))}
          </div>

          <div className="flex flex-col gap-1">
            <label className="font-dm text-xs font-bold text-[rgba(0,0,0,0.55)]">Override note (required)</label>
            <textarea
              {...register('override_note')}
              rows={3}
              placeholder="Provide a rationale for this override decision…"
              className="w-full rounded-lg border border-[rgba(204,204,204,0.7)] px-3 py-2 font-dm text-sm resize-none focus:outline-none focus:border-[#0f1b2a] transition-colors"
            />
            {errors.override_note && <p className="font-dm text-xs text-[#d93025]">{errors.override_note.message}</p>}
          </div>

          <button
            type="submit"
            disabled={override.isPending}
            className="self-start px-5 py-2.5 rounded-lg bg-[#0f1b2a] text-white font-dm text-sm font-bold hover:bg-[#3D5585] transition-colors disabled:opacity-50"
          >
            {override.isPending ? 'Recording…' : 'Record Override'}
          </button>
        </form>
      )}

      {/* Return to sessions */}
      <div className="pb-4">
        <button
          onClick={() => navigate(Routes.ADMIN)}
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg border border-[rgba(204,204,204,0.7)] font-dm text-sm font-bold text-[rgba(0,0,0,0.65)] hover:bg-[#f4f9ff] transition-colors"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Return to sessions
        </button>
      </div>
    </div>
  );
};

// ── sub-components ────────────────────────────────────────────────────────────

const Card: FC<{ icon?: React.ReactNode; title: string; children: React.ReactNode }> = ({ icon, title, children }) => (
  <div className="flex flex-col gap-3 px-6 py-5 rounded-2xl border border-[rgba(204,204,204,0.5)] bg-white">
    <div className="flex items-center gap-2 text-[rgba(0,0,0,0.87)]">
      {icon}
      <h2 className="font-newsreader text-[22px] font-extrabold">{title}</h2>
    </div>
    {children}
  </div>
);

const MetaItem: FC<{ icon?: React.ReactNode; label: React.ReactNode }> = ({ icon, label }) => (
  <div className="flex items-center gap-1.5 font-dm text-sm text-[rgba(0,0,0,0.65)]">
    {icon}
    <span>{label}</span>
  </div>
);

const Sep: FC = () => <span className="text-[rgba(0,0,0,0.2)] select-none font-dm text-sm">·</span>;

export default AdminSessionDetailPage;
